import { some, forEach } from 'lodash';
import React from 'react';

import connect from 'components/lib/connect';
import FeatureNotification from './feature_notification';
import FeatureNotificationTypes from 'scripts/domain/models/agent_notification/feature_notification_types';
import useIsFeatureEnabled from 'components/hooks/use_is_feature_enabled';

const answerThreadsCourse = {
  feature: FeatureNotificationTypes.AT_COURSE,
  roleFlags: ['teamManagement'],
  key: 'at-course-notification',
  text: 'Answer Threads Overview and Set Up Course',
  subtext:
    'Learn about our newest, AI-powered product, Answer Threads. This course will teach you how to configure, manage and optimize Answer Threads for success.',
  url: 'https://connect.gladly.com/courses/answer-threads-overview-and-setup/',
  urlText: 'ENROLL NOW',
};

const reportsAndInsightsClassroom2 = {
  feature: FeatureNotificationTypes.REPORTS_AND_INSIGHTS,
  roleFlags: ['teamManagement'],
  key: 'reports-insights-classroom',
  text: 'Reports and Insights Classroom',
  subtext:
    'Explore the importance of metrics and KPIs in your contact center and how to get the insights you need using Gladly OOTB Reports in this Classroom session.',
  url: 'https://gladly.zoom.us/meeting/register/tZcscO6hqzouEtEPzO4thEOdwZ3VCTladM5x',
  urlText: 'Register',
  endTime: 1730390340000,
};

const answerThreadsClassroom = {
  feature: FeatureNotificationTypes.AT_CLASSROOM,
  roleFlags: ['teamManagement'],
  flag: 'atClassroomNotification',
  key: 'at-classroom-notification',
  text: 'Set Up Answer Threads for Success',
  subtext:
    'Join us in this Classroom session to explore how to ensure Answer Threads operates at its peak and identify opportunities to optimize your Answers, making Answer Threads a true extension of great service.',
  url: 'https://gladly.zoom.us/meeting/register/tZEvc-2rqj8rEtEGjM4-Ba_Ng1lQof8qQaMb',
  urlText: 'REGISTER',
  endTime: 1729177200000,
};

const skDashboard = {
  feature: FeatureNotificationTypes.SK_DASHBOARD,
  roleFlags: ['teamManagement'],
  flag: 'sidekickNotifications',
  key: 'sk-dashboard-notification',
  text: 'Unlock Sidekick Dashboard Insights',
  subtext:
    'Discover how to measure success and identify improvement opportunities in your Sidekick implementation in just 4 minutes.',
  url:
    'https://connect.gladly.com/docs/help-documentation/article/sidekick-dashboard/#access-the-daily-trends-dashboard',
  urlText: 'WATCH NOW',
  endTime: 1729745999000,
};

const CURRENT_FEATURES = [answerThreadsCourse, answerThreadsClassroom, skDashboard, reportsAndInsightsClassroom2];

export function FeatureNotificationList({ agentPreferences, organization }) {
  const isFeatureEnabled = useIsFeatureEnabled();

  let featureNotifications = [];
  forEach(CURRENT_FEATURES, featureProps => {
    if (shouldShowNotification(featureProps, agentPreferences, isFeatureEnabled, organization)) {
      featureNotifications.push(<FeatureNotification {...featureProps} />);
    }
  });

  if (!featureNotifications.length) {
    return null;
  }

  return <React.Fragment>{featureNotifications}</React.Fragment>;
}

const FeatureNotificationListContainer = connect(mapStateToProps)(FeatureNotificationList);

function mapStateToProps({ getProvider }) {
  const agentPreferences = getProvider('agentPreferences').get();
  const organization = getProvider('auth').get()?.claims;

  return {
    agentPreferences,
    organization,
  };
}

export function useHasFeaturesToShow(agentPreferences, organization) {
  const isFeatureEnabled = useIsFeatureEnabled();
  return some(CURRENT_FEATURES, featureProps => {
    return shouldShowNotification(featureProps, agentPreferences, isFeatureEnabled, organization);
  });
}

function shouldShowNotification(featureProps, agentPreferences, isFeatureEnabled, organization) {
  const roleRestricted = featureProps.roleFlags && featureProps.roleFlags.length;
  const restrictedAndHasRole = some(featureProps.roleFlags, roleFlag => isFeatureEnabled(roleFlag));
  const flagRestricted = featureProps.flag;

  const orgRestricted = !!featureProps.orgIds;
  const belongsToOrg = some(featureProps.orgIds, orgId => orgId === organization?.orgId);

  const currentTime = Date.now();
  const startRestricted = !!featureProps.startTime;
  const afterStart = startRestricted ? currentTime > featureProps.startTime : false;
  const endRestricted = !!featureProps.endTime;
  const beforeEnd = endRestricted ? currentTime < featureProps.endTime : false;

  return (
    (!orgRestricted || belongsToOrg) &&
    (!roleRestricted || restrictedAndHasRole) &&
    (!flagRestricted || isFeatureEnabled(featureProps.flag)) &&
    (!startRestricted || afterStart) &&
    (!endRestricted || beforeEnd) &&
    !agentPreferences.hasSeenFeatureNotification(featureProps.feature)
  );
}

export default FeatureNotificationListContainer;
